import { useState, useEffect, useRef, useMemo, useCallback } from "react";

const CatchphraserCommunity = () => {
  const [activeDeck, setActiveDeck] = useState("overview");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [paceMode, setPaceMode] = useState("guided");
  const intervalRef = useRef(null);

  const paceModes = {
    demo: { label: "Demo", multiplier: 0.65, description: "fast overview" },
    guided: { label: "Guided", multiplier: 1, description: "presentation pace" },
    reading: { label: "Reading", multiplier: 1.8, description: "self-paced viewing" },
  };

  const asset = (name) =>
    `${process.env.REACT_APP_USER_API_URL}/uploads/screens/${name}`;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const LinkIcon = () => (
    <svg
      className="mr-1.5 h-3 w-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );

  const ExternalLink = ({ compact = false }) => (
    <a
      href="https://catchphraser.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center rounded-full border border-blue-200 bg-white/85 font-semibold text-blue-700 shadow-sm backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-md ${
        compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
      }`}
    >
      <LinkIcon />
      catchphraser.com
    </a>
  );

  const DesktopLink = () => (
    <div className="absolute right-4 top-4 z-20 hidden sm:block">
      <ExternalLink />
    </div>
  );

  const MobileLink = () => (
    <div className="mb-3 flex justify-center sm:hidden">
      <ExternalLink compact />
    </div>
  );

  const Pill = ({ children, color = "blue" }) => {
    const colors = {
      blue: "bg-blue-50 text-blue-700 ring-blue-200",
      purple: "bg-purple-50 text-purple-700 ring-purple-200",
      green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      pink: "bg-pink-50 text-pink-700 ring-pink-200",
      gray: "bg-gray-50 text-gray-700 ring-gray-200",
      amber: "bg-amber-50 text-amber-700 ring-amber-200",
      cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ${colors[color]}`}
      >
        {children}
      </span>
    );
  };

  const SoftCard = ({ children, className = "" }) => (
    <div
      className={`rounded-3xl border border-white/70 bg-white/85 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  );

  const EmojiCard = ({ emoji, title, text, className = "" }) => (
    <SoftCard className={`text-center ${className}`}>
      <div className="mb-3 text-4xl">{emoji}</div>
      <h4 className="mb-2 text-lg font-black text-gray-950">{title}</h4>
      {text && <p className="text-sm leading-relaxed text-gray-600">{text}</p>}
    </SoftCard>
  );

  const MiniFlow = ({ items }) => (
    <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-3">
      {items.map(([emoji, title], index) => (
        <div
          key={title}
          className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/85 px-4 py-3 shadow-sm"
        >
          <div className="text-2xl">{emoji}</div>
          <div className="text-sm font-bold text-gray-800">{title}</div>
          {index < items.length - 1 && (
            <div className="ml-2 hidden text-gray-300 sm:block">→</div>
          )}
        </div>
      ))}
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div className="mx-auto mb-4 inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-gray-500 shadow-sm">
      {children}
    </div>
  );

  const ConceptMark = ({ emoji = "🎤", label = "Speech", sub = "practice" }) => (
    <div className="mx-auto mb-5 flex flex-col items-center">
      <div className="flex h-24 w-24 items-center justify-center rounded-[1.75rem] bg-gradient-to-br from-blue-600 to-purple-600 text-5xl shadow-[0_22px_60px_rgba(15,23,42,0.18)] ring-1 ring-white/50 sm:h-28 sm:w-28 sm:text-6xl">
        {emoji}
      </div>
      <div className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-gray-500">
        {label}
      </div>
      <div className="text-xs font-semibold text-gray-400">{sub}</div>
    </div>
  );

  const ScreenshotFrame = ({
    src,
    alt,
    caption,
    className = "",
    imgClassName = "",
  }) => (
    <div
      className={`rounded-[2rem] border border-gray-200/80 bg-white p-3 shadow-[0_25px_80px_rgba(15,23,42,0.12)] ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className={`w-full rounded-[1.35rem] border border-gray-100 object-cover ${imgClassName}`}
      />
      {caption && (
        <div className="px-2 pt-3 text-center text-xs font-medium text-gray-500">
          {caption}
        </div>
      )}
    </div>
  );

  const NumberedList = ({ items, accent = "blue" }) => {
    const color =
      accent === "purple"
        ? "text-purple-600"
        : accent === "green"
        ? "text-emerald-600"
        : accent === "amber"
        ? "text-amber-600"
        : "text-blue-600";

    return (
      <div className="space-y-3 text-left">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm ${color}`}
            >
              {index + 1}
            </div>
            <div className="leading-relaxed text-gray-700">{item}</div>
          </div>
        ))}
      </div>
    );
  };

  const ComparisonTable = ({
    leftTitle,
    rightTitle,
    rows,
    leftColor = "blue",
    rightColor = "purple",
  }) => {
    const leftClasses =
      leftColor === "green"
        ? "from-emerald-50 to-white ring-emerald-200"
        : leftColor === "amber"
        ? "from-amber-50 to-white ring-amber-200"
        : "from-blue-50 to-white ring-blue-200";

    const rightClasses =
      rightColor === "pink"
        ? "from-pink-50 to-white ring-pink-200"
        : rightColor === "green"
        ? "from-emerald-50 to-white ring-emerald-200"
        : "from-purple-50 to-white ring-purple-200";

    return (
      <div className="grid w-full gap-5 md:grid-cols-2">
        <div className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${leftClasses}`}>
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">
            {leftTitle}
          </h4>
          <NumberedList items={rows.map((row) => row[0])} accent={leftColor} />
        </div>

        <div className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${rightClasses}`}>
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">
            {rightTitle}
          </h4>
          <NumberedList
            items={rows.map((row) => row[1])}
            accent={rightColor === "green" ? "green" : "purple"}
          />
        </div>
      </div>
    );
  };

  const BigStatement = ({ title, children, tone = "blue" }) => {
    const tones = {
      blue: "from-blue-100 via-white to-purple-100 border-blue-200",
      green: "from-emerald-100 via-white to-blue-100 border-emerald-200",
      purple: "from-purple-100 via-white to-pink-100 border-purple-200",
      amber: "from-amber-100 via-white to-orange-100 border-amber-200",
    };

    return (
      <div
        className={`rounded-3xl border bg-gradient-to-r p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${tones[tone]}`}
      >
        <div className="mb-2 text-xl font-black text-gray-950">{title}</div>
        {children && <div className="leading-relaxed text-gray-700">{children}</div>}
      </div>
    );
  };

  const Timeline = ({ steps }) => (
    <div className="grid gap-4 md:grid-cols-3">
      {steps.map(([emoji, label, title, text, tone]) => (
        <SoftCard key={label} className={`text-center ring-1 ${tone}`}>
          <div className="mb-3 text-5xl">{emoji}</div>
          <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-gray-500">
            {label}
          </div>
          <h4 className="mb-3 text-xl font-black text-gray-950">{title}</h4>
          <p className="leading-relaxed text-gray-600">{text}</p>
        </SoftCard>
      ))}
    </div>
  );

  const decks = useMemo(
    () => ({
      overview: {
        label: "Overview",
        description: "core pitch",
        slides: [
          {
            title: "",
            density: "medium",
            content: (
              <div className="relative overflow-hidden px-2 py-8 text-center sm:py-14">
                <div className="absolute right-3 top-3 sm:right-4 sm:top-4">
                  <ExternalLink compact />
                </div>

                <div className="mx-auto max-w-5xl">
                  <div className="mb-5 inline-flex rounded-full bg-white/85 px-4 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-blue-700 shadow-sm ring-1 ring-blue-200">
                    peer-driven speech practice
                  </div>

                  <h1 className="font-mono text-4xl font-black leading-[0.95] tracking-tight text-gray-950 sm:text-6xl md:text-7xl">
                    From solo repetition
                    <br />
                    to social speech.
                  </h1>

                  <div className="mx-auto my-7 h-1.5 w-48 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 sm:w-80" />

                  <h2 className="mx-auto max-w-4xl text-2xl font-black leading-tight text-gray-800 sm:text-3xl md:text-5xl">
                    Catchphraser turns pronunciation exercises into peer feedback,
                    shared listening, and rehearsal-based language practice.
                  </h2>

                  <p className="mx-auto mt-7 max-w-3xl text-lg leading-relaxed text-gray-600 sm:text-xl">
                    Learners practice with structured audio, receive AI-supported signals,
                    ask humans to listen, and gradually move toward dialogue, reading groups,
                    and theater-like social exercises.
                  </p>

                  <div className="mt-8 flex flex-wrap justify-center gap-2">
                    <Pill color="blue">pronunciation practice</Pill>
                    <Pill color="cyan">AI support</Pill>
                    <Pill color="purple">human feedback</Pill>
                    <Pill color="green">peer learning</Pill>
                    <Pill color="pink">social rehearsal</Pill>
                  </div>
                </div>
              </div>
            ),
            bgColor:
              "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
          },

          {
            title: "The Problem",
            density: "medium",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🙊" label="Problem" sub="speech practice is isolated" />

                <h3 className="text-3xl font-black text-gray-950 md:text-5xl">
                  Pronunciation practice is often lonely, mechanical, and socially disconnected.
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Apps can train repetition. Teachers can correct. But learners also need
                  a place where speaking becomes shared, visible, repairable, and less intimidating.
                </p>

                <ComparisonTable
                  leftTitle="Typical practice"
                  rightTitle="What spoken language needs"
                  rows={[
                    ["solo repetition", "real listeners"],
                    ["right / wrong signals", "interpretable feedback"],
                    ["private mistakes", "safe social repair"],
                    ["AI conversation", "human response"],
                    ["isolated progress", "shared practice culture"],
                  ]}
                />

                <BigStatement title="The missing layer is not another exercise." tone="purple">
                  It is a social practice environment where learners listen to each other,
                  correct each other, and rehearse together.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
          },

          {
            title: "The Product",
            density: "screenshot",
            content: (
              <div className="mx-auto max-w-6xl space-y-8 px-4 py-6 text-center">
                <ConceptMark emoji="💬" label="Now" sub="working social feedback loop" />

                <h3 className="text-3xl font-black text-gray-950 md:text-5xl">
                  Pronunciation practice becomes collaborative repair.
                </h3>

                <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-700">
                  Learners can ask others to listen, mark unclear speech segments,
                  explain pronunciation issues, and continue improvement through discussion.
                </p>

                <MiniFlow
                  items={[
                    ["🎧", "Practice"],
                    ["📣", "Request review"],
                    ["🟢", "Peer verdict"],
                    ["💬", "Discussion"],
                    ["🔁", "Retry"],
                  ]}
                />

                <ScreenshotFrame
                  src={asset("rated.png")}
                  alt="Peer pronunciation review with marked segments and discussion"
                  caption="Learner request → peer verdict → explanation → repair discussion"
                  className="mx-auto max-w-5xl"
                  imgClassName="max-h-[560px] object-contain bg-gray-50"
                />

                <BigStatement title="The key idea is not only AI correction." tone="purple">
                  Catchphraser creates a social layer around pronunciation practice:
                  listening, reviewing, explaining, and collaborative speech repair.
                </BigStatement>
              </div>
            ),
            bgColor:
              "bg-[radial-gradient(circle_at_top_right,_#f3e8ff,_transparent_35%),linear-gradient(135deg,#ffffff,#faf5ff)]",
          },

          {
            title: "The Core Mechanism",
            density: "medium",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="👂" label="Human feedback" sub="speaker + listener" />

                <h3 className="text-3xl font-black text-gray-950 md:text-5xl">
                  AI helps people practice.
                  <br className="hidden md:block" />
                  People help people communicate.
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Human feedback is not just correction. It creates attention, responsibility,
                  motivation, and a real listener behind the exercise.
                </p>

                <ComparisonTable
                  leftTitle="AI support gives"
                  rightTitle="Human feedback gives"
                  rows={[
                    ["safe repetition", "real listener judgment"],
                    ["instant signals", "meaningful explanation"],
                    ["phonetic hints", "social interpretation"],
                    ["private practice", "shared responsibility"],
                    ["unlimited attempts", "motivation to improve"],
                  ]}
                />

                <BigStatement title="This is the platform’s central idea." tone="green">
                  Structured exercises become social when learners can ask others to listen,
                  comment, explain, and help them repair speech.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
          },

          {
  title: "Now, Next, Later",
  density: "medium",
  content: (
    <div className="mx-auto max-w-6xl space-y-8 px-4 py-7 text-center">
      <ConceptMark emoji="🗺️" label="Roadmap" sub="from exercise to rehearsal" />

      <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
        The roadmap moves from individual speech practice to guided social rehearsal.
      </h3>

      <div className="grid items-stretch gap-5 lg:grid-cols-3">
        {[
          {
            badge: "Now",
            emoji: "🎧",
            title: "Exercises + Feedback",
            text: "Build accuracy and awareness with structured practice, AI-supported signals, and peer feedback.",
            items: [
              "Pronunciation tasks",
              "Echoing & shadowing",
              "Prosody view",
              "Comments and peer review",
            ],
            tone:
              "border-blue-200 bg-gradient-to-br from-blue-50/90 to-white ring-blue-100",
            badgeTone: "bg-blue-600 text-white",
            iconTone: "bg-blue-100 text-blue-700",
          },
          {
            badge: "Next",
            emoji: "👥",
            title: "Groups + Guided Rooms",
            text: "Move from solo practice to interaction in structured, supportive group environments.",
            items: [
              "Reading circles",
              "Dialogue practice",
              "Community challenges",
              "Guided speaking rooms",
            ],
            tone:
              "border-purple-200 bg-gradient-to-br from-purple-50/90 to-white ring-purple-100",
            badgeTone: "bg-purple-600 text-white",
            iconTone: "bg-purple-100 text-purple-700",
          },
          {
            badge: "Later",
            emoji: "🎭",
            title: "Theater-like Practice",
            text: "Develop expressive, responsive speech through role-based rehearsal and performance formats.",
            items: [
              "Radio plays",
              "Role-based rehearsal",
              "Expressive reading",
              "Turn-taking & emotion",
            ],
            tone:
              "border-pink-200 bg-gradient-to-br from-pink-50/90 to-white ring-pink-100",
            badgeTone: "bg-pink-600 text-white",
            iconTone: "bg-pink-100 text-pink-700",
          },
        ].map((step, index) => (
          <div key={step.badge} className="relative">
            {index > 0 && (
              <div className="absolute -left-8 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-purple-100 bg-white text-2xl font-black text-purple-500 shadow-lg lg:flex">
                →
              </div>
            )}

            <div
              className={`h-full rounded-[2rem] border p-6 text-left shadow-[0_18px_60px_rgba(15,23,42,0.07)] ring-1 ${step.tone}`}
            >
              <div className="mb-6 flex items-center justify-between gap-4">
                <span
                  className={`rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-[0.12em] shadow-sm ${step.badgeTone}`}
                >
                  {step.badge}
                </span>

                <div
                  className={`flex h-16 w-16 items-center justify-center rounded-2xl text-4xl shadow-sm ${step.iconTone}`}
                >
                  {step.emoji}
                </div>
              </div>

              <h4 className="mb-3 text-2xl font-black text-gray-950">
                {step.title}
              </h4>

              <p className="mb-5 min-h-[84px] text-base leading-relaxed text-gray-650">
                {step.text}
              </p>

              <div className="h-px w-full bg-gray-200/80" />

              <div className="mt-5 space-y-3">
                {step.items.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-black text-gray-700 shadow-sm ring-1 ring-gray-200">
                      ✓
                    </div>
                    <div className="text-sm font-semibold leading-relaxed text-gray-700">
                      {item}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mx-auto grid max-w-5xl items-center gap-5 rounded-[2rem] border border-purple-200 bg-gradient-to-r from-purple-100 via-white to-pink-100 p-6 text-left shadow-[0_18px_60px_rgba(15,23,42,0.08)] md:grid-cols-[auto_1fr]">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-white text-5xl shadow-sm ring-1 ring-purple-100">
          🎯
        </div>

        <div>
          <div className="mb-2 text-2xl font-black text-gray-950">
            The goal is not only correct pronunciation.
          </div>
          <p className="text-lg leading-relaxed text-gray-700">
            The goal is socially alive speech: timing, rhythm, response,
            confidence, and expressive communication.
          </p>
        </div>
      </div>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),radial-gradient(circle_at_bottom_right,_#fce7f3,_transparent_32%),linear-gradient(135deg,#ffffff,#faf5ff)]",
},

          {
  title: "The Ecosystem",
  density: "medium",
  content: (
    <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 text-center">
      <ConceptMark
        emoji="🌐"
        label="Ecosystem"
        sub="social speech infrastructure"
      />

      <h3 className="text-3xl font-black text-gray-950 md:text-5xl">
        Catchphraser can become an ecosystem for social speech practice.
      </h3>

      <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-700">
        The platform is not only for solo learners. It is infrastructure for
        organizing interaction around spoken language, model audio material,
        pronunciation training, and collaborative rehearsal.
      </p>

      <div className="grid gap-5 md:grid-cols-3 lg:grid-cols-6">
        {[
          [
            "🎓",
            "Students",
            "practice pronunciation, listening, rhythm, and expressive speech",
          ],
          [
            "🧑‍🏫",
            "Teachers",
            "organize structured speaking tasks, cohorts, and feedback",
          ],
          [
            "💬",
            "Private tutors",
            "run guided pronunciation and rehearsal activities",
          ],
          [
            "🎭",
            "Theater professionals",
            "coordinate voice, timing, role-based reading, and performance",
          ],
          [
            "🏛️",
            "Universities & language cafés",
            "facilitate peer interaction and collaborative speaking practice",
          ],
          [
            "🎧",
            "Publishers & creators",
            "turn narrated material into interactive speech exercises",
          ],
        ].map(([emoji, title, text]) => (
          <EmojiCard
            key={title}
            emoji={emoji}
            title={title}
            text={text}
            className="ring-1 ring-blue-100"
          />
        ))}
      </div>

      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-3">
        {[
          ["🎧", "Model audio"],
          ["🗣️", "Shadowing"],
          ["🤖", "AI support"],
          ["💬", "Human feedback"],
          ["👥", "Group interaction"],
          ["🎭", "Rehearsal"],
        ].map(([emoji, title], index) => (
          <div
            key={title}
            className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/85 px-4 py-3 shadow-sm"
          >
            <div className="text-2xl">{emoji}</div>

            <div className="text-sm font-bold text-gray-800">
              {title}
            </div>

            {index < 5 && (
              <div className="ml-2 hidden text-gray-300 sm:block">
                →
              </div>
            )}
          </div>
        ))}
      </div>

      <BigStatement
        title="The ecosystem is built around model audio material."
        tone="green"
      >
        Students, teachers, private tutors, theater professionals,
        publishers, universities, and language cafés can organize social
        interactions around structured audio: listening, shadowing,
        feedback, reading groups, and rehearsal.
      </BigStatement>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
},
        ],
      },

      pedagogy: {
        label: "Pedagogy",
        description: "learning theory",
        slides: [
          {
            title: "Speech Is A Coordination Skill",
            density: "dense",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🎼" label="Coordination" sub="ear • voice • rhythm" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Pronunciation is not only knowledge
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Speech involves auditory perception, vocal motor coordination, rhythm,
                  timing, and social responsiveness.
                </p>

                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {[
                    ["👂", "Ear", "perception"],
                    ["🗣️", "Voice", "articulation"],
                    ["🎵", "Rhythm", "timing"],
                    ["👥", "Interaction", "response"],
                  ].map(([emoji, title, text]) => (
                    <EmojiCard
                      key={title}
                      emoji={emoji}
                      title={title}
                      text={text}
                      className="ring-1 ring-blue-100"
                    />
                  ))}
                </div>

                <BigStatement title="Like music, sports, or theater">
                  speech requires repeated embodied practice.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
          },

          {
            title: "Practice vs Rehearsal",
            density: "dense",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🔁" label="Repetition" sub="practice → purpose" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Repetition should lead somewhere
                </h3>

                <ComparisonTable
                  leftTitle="Micro-practice loops"
                  rightTitle="Rehearsal-based learning"
                  leftColor="green"
                  rows={[
                    ["isolated exercises", "preparation for interaction"],
                    ["streak continuity", "cumulative progression"],
                    ["engagement mechanics", "collaborative purpose"],
                    ["correctness-focused", "responsiveness-focused"],
                    ["task completion", "expressive development"],
                  ]}
                />

                <BigStatement title="Repetition is essential for pronunciation." tone="green">
                  But it becomes more meaningful when it prepares the learner for coordination,
                  interaction, and performance.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
          },

          {
            title: "Individual Prosody Training",
            density: "dense",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🎧" label="Prosody" sub="echo • shadow • listen" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Echoing and shadowing train speech internally
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Because the words are already given, learners can focus less on grammar construction
                  and more on pronunciation, rhythm, stress, intonation, and timing.
                </p>

                <ComparisonTable
                  leftTitle="Echoing"
                  rightTitle="Shadowing"
                  rows={[
                    ["listen → pause → repeat", "listen → speak in real time"],
                    ["articulation precision", "rhythm and flow"],
                    ["targeted correction", "speech coordination"],
                    ["fine motor control", "temporal synchronization"],
                  ]}
                />

                <BigStatement title="Human audio models remain central.">
                  They provide rhythm, stress, intonation, and natural speech flow.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#eff6ff,#ecfeff)]",
          },

          {
            title: "Structured Courses",
            density: "screenshot",
            content: (
              <div className="mx-auto max-w-6xl space-y-8 px-4 py-8">
                <div className="text-center">
                  <ConceptMark emoji="🎓" label="Course" sub="structured pedagogy" />

                  <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                    Pronunciation training with pedagogical structure
                  </h3>

                  <p className="mx-auto mt-3 max-w-4xl text-xl text-gray-600">
                    Catchphraser is built on structured pronunciation and prosody pedagogy.
                  </p>
                </div>

                <div className="grid items-center gap-8 lg:grid-cols-2">
                  <SoftCard className="bg-gradient-to-br from-purple-50 to-violet-50 text-center ring-1 ring-purple-100">
                    <div className="mx-auto mb-5 h-36 w-36 overflow-hidden rounded-full border-4 border-white shadow-xl">
                      <img
                        src={asset("oh.jpg")}
                        alt="Associate Professor Olaf Husby"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <h4 className="text-2xl font-black text-gray-950">
                      Assoc. Prof. Olaf Husby
                    </h4>
                    <p className="mt-1 font-bold text-purple-700">
                      Course Author – Teaching & Didactics
                    </p>
                    <p className="mt-4 leading-relaxed text-gray-700">
                      Phonetician, second language educator, course developer,
                      and Norwegian dialect expert.
                    </p>
                  </SoftCard>

                  <div className="space-y-5">
                    <SoftCard>
                      <h4 className="mb-3 text-xl font-black text-gray-950">
                        Flyt og rytme systematically trains
                      </h4>
                      <ul className="space-y-3 leading-relaxed text-gray-700">
                        <li>• sentence rhythm and stress alternation</li>
                        <li>• prosodic foot structure and anacrusis control</li>
                        <li>• chaining across word boundaries</li>
                        <li>• reduction and connected speech processes</li>
                        <li>• echoing → shadowing progression toward automaticity</li>
                      </ul>
                    </SoftCard>

                    <BigStatement title="Structured from foundation to mastery">
                      Courses can function as preparation for later rehearsal, reading groups,
                      and collaborative performance.
                    </BigStatement>
                  </div>
                </div>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
          },
        ],
      },

      technology: {
        label: "Technology",
        description: "product workflows",
        slides: [
          {
            title: "AI-Supported Pronunciation Training",
            density: "screenshot",
            content: (
              <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🤖" label="AI support" sub="practice preparation" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  AI as preparation, not replacement
                </h3>

                <div className="grid items-center gap-8 lg:grid-cols-2">
                  <ScreenshotFrame
                    src={asset("kattenspiserikkefisk.png")}
                    alt="Phoneme accuracy feedback interface"
                    caption="AI-supported pronunciation practice with phonetic feedback"
                    imgClassName="max-h-[430px] object-contain bg-gray-50"
                  />

                  <ComparisonTable
                    leftTitle="AI practice"
                    rightTitle="Human feedback"
                    rows={[
                      ["safe repetition", "real listener response"],
                      ["immediate feedback", "meaningful explanation"],
                      ["pronunciation correction", "social interpretation"],
                      ["unlimited availability", "collaborative attention"],
                    ]}
                  />
                </div>

                <BigStatement title="AI prepares speech.">
                  Human feedback helps activate speech socially.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
          },

          {
            title: "Community Pronunciation Review",
            density: "screenshot",
            content: (
              <div className="mx-auto max-w-6xl px-4 py-8">
                <div className="mb-8 text-center">
                  <SectionLabel>Product Workflow</SectionLabel>
                  <ConceptMark emoji="📣" label="Rate me" sub="human review" />

                  <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                    Learners can ask the community to review pronunciation
                  </h3>

                  <p className="mx-auto mt-4 max-w-3xl text-xl leading-relaxed text-gray-700">
                    The “Rate me” flow turns pronunciation practice into a shared listening task.
                  </p>
                </div>

                <div className="grid items-center gap-8 lg:grid-cols-[1.25fr_0.75fr]">
                  <ScreenshotFrame
                    src={asset("rateme.png")}
                    alt="Rate Me pronunciation review interface"
                    caption="Learner request for structured pronunciation feedback"
                    imgClassName="max-h-[520px] object-contain bg-gray-50"
                  />

                  <div className="space-y-5">
                    <SoftCard className="bg-blue-50/70 ring-1 ring-blue-100">
                      <h4 className="mb-4 text-2xl font-black text-gray-950">
                        What reviewers can do
                      </h4>
                      <NumberedList
                        items={[
                          "replay the learner attempt",
                          "compare it with narrator audio",
                          "inspect rhythm and melody",
                          "mark unclear words or sounds",
                          "leave concrete suggestions",
                        ]}
                      />
                    </SoftCard>

                    <BigStatement title="Feedback is not only correction.">
                      It also trains listening, attention, and pronunciation awareness.
                    </BigStatement>
                  </div>
                </div>
              </div>
            ),
            bgColor:
              "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
          },

          {
            title: "Collaborative Repair & Discussion",
            density: "screenshot",
            content: (
              <div className="mx-auto max-w-6xl space-y-7 px-4 py-8 text-center">
                <div>
                  <SectionLabel>Product Workflow</SectionLabel>
                  <ConceptMark emoji="💬" label="Repair" sub="comment • explain • retry" />

                  <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                    Feedback becomes part of the learning loop
                  </h3>

                  <p className="mx-auto mt-4 max-w-3xl text-xl leading-relaxed text-gray-700">
                    Users can mark pronunciation issues, explain them, reply, and return to practice
                    with a clearer target.
                  </p>
                </div>

                <ScreenshotFrame
                  src={asset("rated.png")}
                  alt="Completed pronunciation rating with marked words and comment discussion"
                  caption="Completed peer verdict with marked segments and discussion"
                  className="mx-auto max-w-5xl"
                  imgClassName="max-h-[560px] object-contain bg-gray-50"
                />

                <BigStatement title="Learners improve not only by speaking." tone="purple">
                  They also improve by learning to hear speech more precisely.
                </BigStatement>
              </div>
            ),
            bgColor:
              "bg-[radial-gradient(circle_at_top_right,_#f3e8ff,_transparent_35%),linear-gradient(135deg,#ffffff,#faf5ff)]",
          },

          {
            title: "Prosody Visualization",
            density: "screenshot",
            content: (
              <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="📈" label="Visualize" sub="pitch • rhythm • stress" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Seeing rhythm, stress, and intonation
                </h3>

                <p className="mx-auto max-w-3xl text-xl text-gray-600">
                  Visual feedback helps learners notice how words form intonation groups and
                  how pitch moves inside a phrase.
                </p>

                <ScreenshotFrame
                  src={asset("muchwarmerthere.png")}
                  alt="Pitch contour visualization showing intonation groups and pitch variation within words"
                  className="mx-auto max-w-5xl"
                  imgClassName="max-h-[520px] object-contain bg-gray-50"
                />

                <BigStatement title="Technology supports perception." tone="purple">
                  The goal is not merely analysis, but socially usable speech.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
          },

          {
            title: "L1-Sensitive Sound Guidance",
            density: "medium",
            content: (
              <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🗺️" label="L1-aware" sub="targeted sound help" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Sound guidance should depend on the learner’s first language
                </h3>

                <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-700">
                  Learners with different L1 backgrounds struggle with different contrasts.
                  Catchphraser can prioritize difficult sounds and explain them through articulation,
                  examples, and closest analogies.
                </p>

                <div className="grid gap-5 md:grid-cols-4">
                  {[
                    ["🌍", "L1 profile", "detect likely pronunciation difficulties"],
                    ["🎯", "Target sounds", "prioritize difficult contrasts"],
                    ["🦷", "Articulation map", "tongue position, alveolar contact, and airflow"],
                    ["📚", "Examples", "connect sound to real phrases"],
                  ].map(([emoji, title, text]) => (
                    <EmojiCard
                      key={title}
                      emoji={emoji}
                      title={title}
                      text={text}
                      className="ring-1 ring-cyan-100"
                    />
                  ))}
                </div>

                <a
                  href="https://www.catchphraser.com/sounds"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-full bg-blue-600 px-5 py-2.5 font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  Open sound prototype →
                </a>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#f8fafc)]",
          },
        ],
      },

      community: {
        label: "Community",
        description: "peer learning & rehearsal",
        slides: [
          {
            title: "Why Peer-Driven Learning Matters",
            density: "dense",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="👥" label="Community" sub="peer-driven learning" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Community is part of the learning mechanism
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Learners do not improve only by hearing perfect speech. They also improve
                  by hearing others struggle, repair, adapt, and gradually become more natural.
                </p>

                <ComparisonTable
                  leftTitle="Native models"
                  rightTitle="Peer participation"
                  rows={[
                    ["show the target", "show the path"],
                    ["ideal pronunciation", "visible gradual improvement"],
                    ["reference", "participation"],
                    ["polished delivery", "realistic variability"],
                  ]}
                />

                <BigStatement title="Peers make progress visible." tone="purple">
                  This can reduce inhibition and create a shared practice culture.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
          },

          {
            title: "Social Feedback Layer",
            density: "dense",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🧠" label="Feedback" sub="speaker + listener" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Feedback trains both speaker and listener
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  When learners evaluate others, they often notice rhythm, stress, unclear sounds,
                  and intonation patterns before recognizing similar issues in themselves.
                </p>

                <div className="grid items-stretch gap-3 text-center md:grid-cols-5">
                  {[
                    ["🎙️", "Practice"],
                    ["📩", "Receive feedback"],
                    ["💬", "Give feedback"],
                    ["👂", "Improve perception"],
                    ["🎯", "Refine speech"],
                  ].map(([emoji, title]) => (
                    <EmojiCard key={title} emoji={emoji} title={title} />
                  ))}
                </div>

                <BigStatement title="Giving feedback can become learning." tone="green">
                  The reviewer practices analytical listening and becomes more aware of their own pronunciation.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
          },

          {
            title: "From Practice To Rehearsal",
            density: "dense",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🎭" label="Rehearsal" sub="dialogue rhythm" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Dialogue turns pronunciation into coordination
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Dialogue reading and rehearsal train turn-taking, timing,
                  emotional responsiveness, and conversational rhythm.
                </p>

                <ComparisonTable
                  leftTitle="Individual training"
                  rightTitle="Collaborative rehearsal"
                  rows={[
                    ["internal rhythm", "shared timing"],
                    ["articulation", "interaction"],
                    ["pronunciation", "coordination"],
                    ["repetition", "responsiveness"],
                    ["isolated production", "social adaptation"],
                  ]}
                />

                <BigStatement title="Native-speaker interaction remains ideal.">
                  But structured peer rehearsal can still provide meaningful communicative growth
                  when native-speaking environments are unavailable.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
          },

          {
            title: "The Rehearsal Loop",
            density: "medium",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🔄" label="Loop" sub="practice → feedback → rehearsal" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Practice → Feedback → Rehearsal → Refinement
                </h3>

                <div className="grid gap-3 md:grid-cols-6">
                  {[
                    ["🎧", "Audio models"],
                    ["🗣️", "Echoing & shadowing"],
                    ["🤖", "AI feedback"],
                    ["💬", "Peer feedback"],
                    ["👥", "Dialogue rehearsal"],
                    ["🎭", "Reading groups / plays"],
                  ].map(([emoji, title]) => (
                    <EmojiCard key={title} emoji={emoji} title={title} className="p-4" />
                  ))}
                </div>

                <SoftCard className="mx-auto max-w-4xl ring-1 ring-purple-100">
                  <div className="mb-3 text-xl font-black text-gray-950">
                    The script reduces grammar pressure.
                  </div>
                  <p className="leading-relaxed text-gray-700">
                    Because the words are already given, learners can focus on delivery:
                    pronunciation, timing, emotion, and response to other actors.
                  </p>
                </SoftCard>

                <BigStatement
                  title="Not isolated exercises — a progression toward expressive communication."
                  tone="purple"
                />
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#fdf2f8)]",
          },
        ],
      },

      business: {
        label: "Business",
        description: "monetization and team",
        slides: [
          {
            title: "Monetization & Partnerships",
            density: "medium",
            content: (
              <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="💼" label="Business" sub="community-first model" />

                <h3 className="text-3xl font-black text-gray-950 md:text-5xl">
                  Monetization follows the learning model.
                </h3>

                <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-700">
                  Catchphraser is not only selling solo exercises. It can become infrastructure
                  for organized speech practice, guided rehearsal, narrated content,
                  institutional speaking activities, and creator-led courses.
                </p>

                <div className="grid gap-5 md:grid-cols-4">
                  <EmojiCard
                    emoji="🎧"
                    title="Publisher subscriptions"
                    text="Audiobook and educational publishers can offer narrated content as interactive pronunciation and rehearsal material."
                    className="ring-1 ring-blue-100"
                  />

                  <EmojiCard
                    emoji="🎓"
                    title="Course marketplace"
                    text="Publishers, course developers, and teachers can sell structured courses, with platform revenue sharing."
                    className="ring-1 ring-amber-100"
                  />

                  <EmojiCard
                    emoji="🧑‍🏫"
                    title="Guided activities"
                    text="Teachers, tutors, actors, and facilitators can organize paid rooms, workshops, and rehearsal groups."
                    className="ring-1 ring-purple-100"
                  />

                  <EmojiCard
                    emoji="🏛️"
                    title="University language cafés"
                    text="Universities and language cafés can structure peer practice, feedback, and speaking groups."
                    className="ring-1 ring-emerald-100"
                  />
                </div>

                <BigStatement
                  title="The first offer: structured social practice and course infrastructure."
                  tone="green"
                >
                  Free peer exchange can bring users in. Paid guided rooms, institutional cohorts,
                  publisher content, creator courses, and specialized workshops create monetization.
                </BigStatement>
              </div>
            ),
            bgColor:
              "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
          },

          {
            title: "Guided Activities",
            density: "medium",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="🏋️‍♀️" label="Guided" sub="speech gym trainers" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Supervised rehearsal with speech “gym trainers”
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Guided activities are not traditional tutoring first. They are structured social practice:
                  speaking rooms, reading circles, pronunciation workshops, and rehearsal sessions.
                </p>

                <div className="grid gap-5 md:grid-cols-3">
                  <EmojiCard
                    emoji="🎭"
                    title="Theater students"
                    text="Voice, timing, emotional delivery, and role work."
                    className="ring-1 ring-blue-100"
                  />

                  <EmojiCard
                    emoji="🗣️"
                    title="Native-speaking guides"
                    text="Natural prosody, pragmatic nuance, and live interactional rhythm."
                    className="ring-1 ring-purple-100"
                  />

                  <EmojiCard
                    emoji="💼"
                    title="Paid group formats"
                    text="Guided rooms, workshops, university partnerships, and supervised activities."
                    className="ring-1 ring-emerald-100"
                  />
                </div>

                <BigStatement title="Not replacing peer learning — enriching it." tone="purple">
                  Facilitators help coordinate practice without turning the platform into a generic tutoring marketplace.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
          },

          {
            title: "Content & Course Platform",
            density: "medium",
            content: (
              <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
                <ConceptMark emoji="📚" label="Courses" sub="publishers • teachers • developers" />

                <h3 className="text-3xl font-black text-gray-950 md:text-4xl">
                  Narrated content and courses become interactive speech practice.
                </h3>

                <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
                  Audiobooks, dialogue recordings, graded readers, pronunciation courses,
                  and teacher-made materials can become echoing, shadowing, feedback,
                  and group rehearsal experiences.
                </p>

                <ComparisonTable
                  leftTitle="Creator value"
                  rightTitle="Learner value"
                  leftColor="amber"
                  rightColor="green"
                  rows={[
                    ["new channel for course distribution", "high-quality speech models"],
                    ["subscription or course revenue", "structured speaking progression"],
                    ["developer portal for materials", "practice with feedback tools"],
                    ["platform handles delivery", "reading + listening + speaking"],
                  ]}
                />

                <BigStatement title="This is not only content licensing." tone="amber">
                  It is turning narrated material and expert-made courses into interactive speech practice infrastructure.
                </BigStatement>
              </div>
            ),
            bgColor: "bg-[linear-gradient(135deg,#ffffff,#fffbeb)]",
          },

          {
            title: "The Catchphraser Team",
            density: "team",
            content: (
              <div className="mx-auto max-w-6xl space-y-10 px-4 py-12 text-center">
                <div className="mb-10 text-center">
                  <h2 className="mb-5 text-4xl font-black text-gray-950 md:text-5xl">
                    The Catchphraser Team
                  </h2>
                  <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-600 md:text-2xl">
                    Researchers and language professionals building pronunciation training and
                    rehearsal infrastructure.
                  </p>
                </div>

                <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {[
                    [
                      "it.jpg",
                      "Dr. Inna Tolskaya",
                      "CEO – Product & Linguistics",
                      "itolsk@gmail.com",
                      "Linguist, project manager, and software developer with a passion for innovation and e-learning.",
                      "from-blue-50 to-indigo-50 border-blue-200 text-blue-600",
                      "IT",
                    ],
                    [
                      "oh.jpg",
                      "Assoc. Prof. Olaf Husby",
                      "CCO – Teaching & Didactics",
                      "olafhusby@gmail.com",
                      "Phonetician, second language educator, course developer, and Norwegian dialects evangelist.",
                      "from-purple-50 to-violet-50 border-purple-200 text-purple-600",
                      "OH",
                    ],
                    [
                      "ds.jpg",
                      "Dr. Dmitry Shcherbin",
                      "CTO/CPO – Tech & Research",
                      "dmitry.shcherbin@gmail.com",
                      "Product leader, software developer, data analyst, and researcher with an interest in practical applications.",
                      "from-cyan-50 to-teal-50 border-cyan-200 text-cyan-600",
                      "DS",
                    ],
                  ].map(([img, name, role, email, bio, cardColor, initials]) => (
                    <div
                      key={name}
                      className={`rounded-3xl border bg-gradient-to-br ${cardColor} p-6 shadow-[0_20px_70px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
                    >
                      <div className="text-center">
                        <div className="relative mx-auto mb-5 flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-xl">
                          <div className="absolute inset-0 flex items-center justify-center text-3xl font-black text-gray-500">
                            {initials}
                          </div>
                          <img
                            src={asset(img)}
                            alt={name}
                            className="relative z-10 h-full w-full object-cover"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        </div>

                        <h3 className="mb-2 text-xl font-black text-gray-950">{name}</h3>
                        <p className="mb-3 text-base font-bold">{role}</p>
                        <a href={`mailto:${email}`} className="break-all text-sm hover:underline">
                          {email}
                        </a>
                      </div>

                      <div className="mt-6 rounded-2xl border border-white/60 bg-white/85 p-5 shadow-sm backdrop-blur-sm">
                        <p className="text-base leading-relaxed text-gray-700">{bio}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 text-center">
                  <p className="mb-2 text-lg text-gray-700">
                    Interested in collaboration, testing, or research partnerships?
                  </p>
                  <a
                    href="mailto:info@catchphraser.com"
                    className="text-lg font-bold text-blue-600 hover:underline"
                  >
                    info@catchphraser.com
                  </a>
                </div>
              </div>
            ),
            bgColor:
              "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ecfeff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
          },
        ],
      },
    }),
    []
  );

  const slides = decks[activeDeck].slides;

  const inferSlideDensity = useCallback(
    (slide, index) => {
      if (slide.density) return slide.density;
      if (activeDeck === "business" && index === slides.length - 1) return "team";
      return "medium";
    },
    [activeDeck, slides.length]
  );

  const getSlideDuration = useCallback(
    (slide, index = currentSlide) => {
      const densityDurations = {
        light: 7000,
        medium: 11000,
        dense: 16000,
        screenshot: 14000,
        team: 18000,
      };

      const density = inferSlideDensity(slide, index);
      const baseDuration =
        slide.duration || densityDurations[density] || densityDurations.medium;

      return Math.round(baseDuration * paceModes[paceMode].multiplier);
    },
    [currentSlide, inferSlideDensity, paceMode]
  );

  const stopSlideshow = useCallback(() => {
    if (intervalRef.current) clearTimeout(intervalRef.current);
    intervalRef.current = null;
    setIsPlaying(false);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((current) => Math.min(current + 1, slides.length - 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((current) => Math.max(current - 1, 0));
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const switchDeck = useCallback(
    (deckKey) => {
      setActiveDeck(deckKey);
      setCurrentSlide(0);
      stopSlideshow();
    },
    [stopSlideshow]
  );

  const playSlideshow = useCallback(() => {
    setIsPlaying((playing) => !playing);
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    if (intervalRef.current) clearTimeout(intervalRef.current);

    intervalRef.current = setTimeout(() => {
      setCurrentSlide((current) => {
        if (current < slides.length - 1) return current + 1;
        setIsPlaying(false);
        return current;
      });
    }, getSlideDuration(slides[currentSlide], currentSlide));

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [isPlaying, currentSlide, activeDeck, paceMode, slides, getSlideDuration]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tag = event.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || event.target?.isContentEditable) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          prevSlide();
          break;

        case "ArrowRight":
        case " ":
          event.preventDefault();
          nextSlide();
          break;

        case "p":
        case "P":
          event.preventDefault();
          playSlideshow();
          break;

        case "Home":
          event.preventDefault();
          goToSlide(0);
          break;

        case "End":
          event.preventDefault();
          goToSlide(slides.length - 1);
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToSlide, nextSlide, playSlideshow, prevSlide, slides.length]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, []);

  const progress = ((currentSlide + 1) / slides.length) * 100;
  const activeDurationSeconds = Math.round(
    getSlideDuration(slides[currentSlide], currentSlide) / 1000
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_transparent_28%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#f8fafc,#f1f5f9)] p-3 text-gray-900 sm:p-4 md:p-6 lg:p-8">
      <div className="mx-auto max-w-6xl">
        {isMobile && currentSlide > 0 && <MobileLink />}

        <div className="mb-4 flex flex-wrap justify-center gap-2">
          {Object.entries(decks).map(([key, deck]) => (
            <button
              key={key}
              onClick={() => switchDeck(key)}
              className={`rounded-full px-4 py-2 text-xs font-black transition-all sm:text-sm ${
                activeDeck === key
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                  : "border border-gray-200 bg-white/80 text-gray-600 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
              }`}
              title={deck.description}
            >
              {deck.label}
            </button>
          ))}
        </div>

        <div className="mb-3 flex flex-col items-start justify-between gap-3 sm:mb-4 lg:flex-row lg:items-center">
          <div className="inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold text-gray-500 shadow-sm backdrop-blur-sm sm:text-sm">
            {decks[activeDeck].label} • {currentSlide + 1} / {slides.length}
            {isPlaying && (
              <span className="ml-2 text-blue-600">• {activeDurationSeconds}s</span>
            )}
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
            <div className="flex rounded-full border border-gray-200 bg-white/80 p-1 shadow-sm backdrop-blur-sm">
              {Object.entries(paceModes).map(([key, mode]) => (
                <button
                  key={key}
                  onClick={() => setPaceMode(key)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-black transition-all sm:text-xs ${
                    paceMode === key
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                  title={mode.description}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              {isPlaying && (
                <div className="flex items-center rounded-full border border-emerald-200 bg-white/80 px-3 py-1 text-emerald-700 shadow-sm">
                  <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                  <span className="text-xs font-bold sm:text-sm">Playing</span>
                </div>
              )}

              <button
                onClick={playSlideshow}
                className={`flex flex-1 items-center justify-center rounded-full px-4 py-2 text-xs font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl sm:flex-none sm:text-sm ${
                  isPlaying
                    ? "bg-gradient-to-r from-red-500 to-orange-500"
                    : "bg-gradient-to-r from-blue-600 to-purple-600"
                }`}
              >
                {isPlaying ? "Pause" : "Play"}
              </button>
            </div>
          </div>
        </div>

        <div className="mb-4 h-2 overflow-hidden rounded-full bg-white/70 shadow-inner ring-1 ring-gray-200 sm:mb-6">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div
          className={`relative overflow-hidden rounded-[1.75rem] shadow-[0_30px_100px_rgba(15,23,42,0.14)] ring-1 ring-black/5 transition-all duration-300 sm:rounded-[2rem] ${slides[currentSlide].bgColor}`}
        >
          {!isMobile && currentSlide > 0 && <DesktopLink />}

          <div className="flex min-h-[60vh] flex-col p-4 sm:min-h-[70vh] sm:p-6 md:p-8 lg:p-12">
            {slides[currentSlide].title && (
              <div className="mb-5 text-center sm:mb-7">
                <h2 className="font-mono text-lg font-black tracking-tight text-gray-950 sm:text-xl md:text-2xl lg:text-3xl">
                  {slides[currentSlide].title}
                </h2>
                <div className="mx-auto mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 sm:w-28" />
              </div>
            )}

            <div className="flex flex-grow items-center overflow-y-auto">
              <div className="w-full">{slides[currentSlide].content}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 sm:mt-6">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm ${
              currentSlide === 0
                ? "cursor-not-allowed text-gray-400"
                : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            ← Previous
          </button>

          <div className="flex max-w-[55%] flex-wrap justify-center gap-1.5">
            {slides.map((_, index) => (
              <button
                key={`${activeDeck}-${index}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 rounded-full transition-all ${
                  currentSlide === index
                    ? isPlaying
                      ? "w-6 animate-pulse bg-emerald-500"
                      : "w-6 bg-blue-600"
                    : "w-2 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm ${
              currentSlide === slides.length - 1
                ? "cursor-not-allowed text-gray-400"
                : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            {currentSlide === slides.length - 1 ? "Finish" : "Next →"}
          </button>
        </div>

        <div className="mt-3 text-center text-xs font-medium text-gray-400 sm:mt-4">
          ← → to navigate • Space to advance • P to play/pause
        </div>
      </div>
    </div>
  );
};

export default CatchphraserCommunity;